// Modules
import React, { useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { ErrorResponse } from 'apollo-link-error'

// Application
import { useAccountMDMHook } from 'hooks'
import { Contact, ContactType } from 'typings'
import { useApp } from 'context'
import { OutputErrorCode, OutputErrorMsg, ROUTES_PATH } from 'constants/index'
import { ConfirmContact } from 'components/Presenters/ConfirmContact'
import { formattedUrl } from 'utils'
import { useSignUpStepper } from 'redesign/hooks/useSignUpStepper'
import { useSignUpAnaytics } from 'hooks/useSignUpAnaytics'

// Components
import { TemplateContainer } from 'components'
import { SelectContact, SelectContactInitial } from 'components/Presenters/SelectContact'
import { SignUpHeader as Header } from 'redesign/components/organisms'
import { useRemoteConfig } from 'modules/RemoteConfig/hooks/useRemoteConfig'
import { BiometyDataType } from 'typings/unico'
import { UNICO_CONSTANTS } from 'redesign/constants'

export const MDMSelectContact: React.VFC = () => {
    const accountMDM = useAccountMDMHook()
    const history = useHistory()
    const location = useLocation()
    const { isFeatureEnabled } = useRemoteConfig()
    const { openToast } = useApp()

    const { setStepValue, size } = useSignUpStepper()
    const IS_UNICO_ENABLED = isFeatureEnabled('AUTH_ENABLE_UNICO')
    const biometyDataLS: BiometyDataType = JSON.parse(sessionStorage.getItem(UNICO_CONSTANTS.STORAGE)!)
    const usernameToRecover = JSON.parse(sessionStorage.getItem('@LiviaRecoverPassword:Username')!)

    const shouldRedirect = !accountMDM?.cpf || !accountMDM?.biologicalSex || !accountMDM?.password

    !IS_UNICO_ENABLED &&
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useSignUpAnaytics({
            hasMDM: true,
            defaultDispatchEvent: 'selectContactOnOpen',
            shouldCall: accountMDM.hasMounted && !shouldRedirect,
        })

    useEffect(() => {
        if (size === 3) {
            setStepValue(1)
            return
        }
        setStepValue(2)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        sessionStorage.setItem(
            UNICO_CONSTANTS.STORAGE,
            JSON.stringify({
                ...biometyDataLS,
                origin: undefined,
            }),
        )
    }, [biometyDataLS])

    if (!accountMDM.hasMounted) {
        return null
    }

    if (shouldRedirect && !usernameToRecover) {
        history.replace(ROUTES_PATH.SIGN_IN)
        return null
    }

    const handleSubmit = async (values: SelectContactInitial) => {
        try {
            accountMDM.setSubmitting(true)

            const contactType = values.contact?.contactType === 'EMAIL' ? ContactType.EMAIL : ContactType.MOBILE
            const contactData: Contact = {
                id: values.contact?.id,
                value: values.contact?.value,
                contactType,
            }

            await accountMDM.setContact(contactData)
            const patient = {
                password: accountMDM.password,
                cpf: accountMDM.cpf,
                contactId: contactData.id,
                biologicalSex: accountMDM.biologicalSex,
                cep: accountMDM.postalCode,
                destinationToken: contactType,
            }

            await accountMDM.createLogin(patient)
            accountMDM.setSubmitting(false)
            history.push(formattedUrl(location, 'teste', ROUTES_PATH.SIGN_UP_MDM_VALIDATE_TOKEN))
        } catch (err) {
            const e: ErrorResponse = err

            if (e.graphQLErrors) {
                const error = e.graphQLErrors[0].extensions?.body.error || e.graphQLErrors[0].extensions?.code

                if (error === OutputErrorCode.PHONEALREADYINUSE || error === OutputErrorCode.EMAILALREADYINUSE) {
                    openToast(OutputErrorMsg.PHONEALREADYINUSE)
                } else {
                    openToast(OutputErrorMsg.GENERICERROR)
                }
                return
            }
        }
    }

    const contacts: Array<Contact> = []
    contacts.push(...(accountMDM.phoneContacts || []))
    contacts.push(...(accountMDM.emailContacts || []))

    if (contacts.length > 0) {
        return (
            <TemplateContainer>
                <Header
                    showStepper
                    subtitle="Escolha um desses contatos para receber o código de validação da sua conta Nav Dasa:"
                    title="Confirme que é você"
                />
                <SelectContact
                    contacts={contacts}
                    handleSubmit={handleSubmit}
                    initialValues={{ contact: contacts[0] }}
                    isSubmitting={accountMDM.isSubmitting}
                />
            </TemplateContainer>
        )
    }

    return <ConfirmContact nextUrl="/login" />
}
